import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, FileText } from 'lucide-react';
import { getSession } from '../services/api';
import websocketService from '../services/websocket';
import PDFSlideViewer from '../components/PDFSlideViewer';

const ViewerDepth = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [presentation, setPresentation] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load session and presentation
    const loadSession = async () => {
      try {
        const data = await getSession(sessionId);
        setSession(data.session);
        setPresentation(data.presentation);
        setCurrentSlide(data.presentation.slides[data.session.current_slide]);
        setLoading(false);
      } catch (error) {
        console.error('Error loading session:', error);
        setLoading(false);
      }
    };

    loadSession();
  }, [sessionId]);

  useEffect(() => {
    if (!presentation) return;

    let pollInterval = null;
    let isWebSocketWorking = false;

    // Connect to WebSocket for real-time updates
    const setupWebSocket = async () => {
      try {
        await websocketService.connect();
        await websocketService.joinSession(sessionId);
        console.log('ViewerDepth: WebSocket setup complete');
        isWebSocketWorking = true;
      } catch (error) {
        console.error('ViewerDepth: WebSocket setup failed, using polling fallback:', error);
        isWebSocketWorking = false;
      }
    };

    setupWebSocket();

    // Polling fallback
    const startPollingFallback = () => {
      pollInterval = setInterval(async () => {
        if (isWebSocketWorking) return;
        
        try {
          const data = await getSession(sessionId);
          
          setSession(prev => {
            if (prev.current_slide !== data.session.current_slide) {
              console.log('Polling: Slide changed to', data.session.current_slide);
              setCurrentSlide(data.presentation.slides[data.session.current_slide]);
            }
            return data.session;
          });
          
          setPresentation(prevPres => {
            const hasChanges = JSON.stringify(prevPres.slides) !== JSON.stringify(data.presentation.slides);
            if (hasChanges) {
              setCurrentSlide(prevSlide => {
                const currentIndex = prevPres.slides.findIndex(s => s.id === prevSlide.id);
                return data.presentation.slides[currentIndex] || prevSlide;
              });
              return data.presentation;
            }
            return prevPres;
          });
        } catch (error) {
          console.error('Polling error:', error);
        }
      }, 200); // Poll every 200ms (0.2 seconds)
    };

    const fallbackTimeout = setTimeout(() => {
      if (!websocketService.isSocketConnected()) {
        console.log('ViewerDepth: Starting polling fallback');
        startPollingFallback();
      }
    }, 3000);

    // Listen for slide changes
    const handleSlideChange = (data) => {
      if (data.session_id === sessionId) {
        console.log('WebSocket: Slide change received');
        isWebSocketWorking = true;
        
        setSession(prev => ({ ...prev, current_slide: data.current_slide }));
        setPresentation(prevPres => {
          if (prevPres && prevPres.slides[data.current_slide]) {
            setCurrentSlide(prevPres.slides[data.current_slide]);
          }
          return prevPres;
        });
      }
    };

    // Listen for depth notes updates
    const handleDepthNotesUpdate = (data) => {
      isWebSocketWorking = true;
      
      setPresentation(prevPres => {
        if (!prevPres) return prevPres;
        
        const updatedSlides = prevPres.slides.map(slide =>
          slide.id === data.slide_id ? { ...slide, depth_notes: data.depth_notes } : slide
        );
        
        setCurrentSlide(prevSlide => 
          prevSlide && prevSlide.id === data.slide_id 
            ? { ...prevSlide, depth_notes: data.depth_notes } 
            : prevSlide
        );
        
        return { ...prevPres, slides: updatedSlides };
      });
    };

    websocketService.onSlideChange(handleSlideChange);
    websocketService.onDepthNotesUpdate(handleDepthNotesUpdate);

    return () => {
      clearTimeout(fallbackTimeout);
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      websocketService.removeListener('slide_change');
      websocketService.removeListener('depth_notes_update');
      websocketService.leaveSession(sessionId);
    };
  }, [sessionId, presentation]);

  const goBackToMain = () => {
    navigate(`/view/${sessionId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-600">Loading depth content...</p>
      </div>
    );
  }

  if (!session || !presentation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-600">Content not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="container mx-auto px-6 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={goBackToMain}
            variant="ghost"
            className="mb-4 gap-2 text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Main Slide
          </Button>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
            {presentation.title}
          </h1>
          <p className="text-slate-600 text-sm mt-1">Depth View</p>
        </div>

        {/* Current Slide Reference with Thumbnail */}
        <Card className="border-slate-200 shadow-sm mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-slate-700 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Current Slide
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Slide Thumbnail */}
              <div className="md:col-span-1">
                <div className="aspect-video rounded-lg border-2 border-slate-200 overflow-hidden shadow-sm bg-white">
                  {presentation.pdf_data && currentSlide?.pdf_page_number > 0 ? (
                    <PDFSlideViewer 
                      key={`depth-slide-${session.current_slide}-page-${currentSlide.pdf_page_number}`}
                      pdfData={presentation.pdf_data}
                      pageNumber={currentSlide.pdf_page_number}
                      maxWidth={400}
                    />
                  ) : (
                    <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-6 h-full flex flex-col justify-center">
                      <h3 className="text-lg font-bold text-slate-900 mb-3 line-clamp-2">
                        {currentSlide?.title}
                      </h3>
                      <p className="text-sm text-slate-600 line-clamp-4">
                        {currentSlide?.content}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Slide Info */}
              <div className="md:col-span-2 flex flex-col justify-center">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">{currentSlide?.title}</h2>
                <p className="text-slate-600 text-sm">
                  Slide {(session.current_slide || 0) + 1} of {presentation.slides.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Depth Content */}
        <Card className="border-blue-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-slate-900">Speaker Notes</CardTitle>
          </CardHeader>
          <CardContent>
            {currentSlide?.depth_notes ? (
              <div className="prose max-w-none">
                <p className="text-slate-700 text-lg leading-relaxed whitespace-pre-wrap">
                  {currentSlide.depth_notes}
                </p>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-slate-500">No speaker notes available for this slide</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Additional Resources */}
        {currentSlide?.additional_resources && (
          <Card className="border-slate-200 shadow-sm mt-6">
            <CardHeader>
              <CardTitle className="text-slate-900 text-lg">Additional Resources</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {currentSlide.additional_resources.split('\n').filter(line => line.trim()).map((resource, index) => {
                  const isUrl = resource.match(/^https?:\/\//);
                  const isImageUrl = resource.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i);
                  
                  if (isImageUrl) {
                    return (
                      <div key={index} className="rounded-lg overflow-hidden border border-slate-200">
                        <img 
                          src={resource} 
                          alt={`Resource ${index + 1}`}
                          className="w-full h-auto"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <p style={{ display: 'none' }} className="p-4 text-sm text-slate-600">
                          Image failed to load: {resource}
                        </p>
                      </div>
                    );
                  } else if (isUrl) {
                    return (
                      <a
                        key={index}
                        href={resource}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:underline"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        {resource}
                      </a>
                    );
                  } else {
                    return (
                      <p key={index} className="text-slate-700">
                        {resource}
                      </p>
                    );
                  }
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Slide Counter */}
        <div className="mt-8 text-center">
          <p className="text-slate-500 text-sm">
            Slide {(session.current_slide || 0) + 1} of {presentation.slides.length}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ViewerDepth;
